import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import Button from 'components/atoms/Button';
import { OS_LIST, OTHER_PLATFORM } from '../../constants';
import { getPlatformFamilyName, getPlatformName, isIosOrAndroid, isSiteReadyToSave } from '../../utils';
import AdvancedSettings from '../AdvancedSettings';
import { MANUAL_PUBLIC_ID_PLATFORMS, STORE_SELECTION_PLATFORMS } from '../appCardUtils';
import { DuplicateLongnameMessage } from '../messages';
import css from '../AppCard.css';
import FraudControls from './FraudControls';
import PlatformSelector from './PlatformSelector';
import SiteIdentityFields from './SiteIdentityFields';
import {
    appsPermissionsPropType,
    configurationPropType,
    platformPropType,
    sitePropType,
    userDataPropType,
} from './propTypes';

const STORE_DETAILS_DEBOUNCE_MS = 1000;

const SiteDetails = ({
    site,
    appId,
    appName,
    configuration,
    appsPermissions,
    adminMode,
    translate,
    userData,
    readOnly,
    onSitePatch,
    onRefreshStoreDetails,
    onTestFcmIntegration,
    onJumpToDuplicateSite,
    onRestoreDuplicateSite,
    onSave,
    onCancel,
    siteDirty,
    platformNames,
    platforms,
    onTrackAppsEvent,
}) => {
    const platformName = getPlatformName(site);
    const platformFamilyName = getPlatformFamilyName(site);
    const concretePlatformName = site?.platform?.name || site?.platform || platformName;
    const isOtherBucketSite =
        concretePlatformName === OTHER_PLATFORM || !OS_LIST.includes(String(concretePlatformName));
    const selectedBucketPlatform = isOtherBucketSite ? OTHER_PLATFORM : platformFamilyName;
    const validationErrors = site.validationErrors || {};
    const hasValidationErrors = Object.keys(validationErrors).length > 0;
    const isPublished = site._published !== false;
    const isDraftSite = !site.id;
    const isManualPublicIdPlatform = MANUAL_PUBLIC_ID_PLATFORMS.includes(platformName);
    const displayAsPublished = !isManualPublicIdPlatform && isPublished;
    const showSelectionMethod = isDraftSite && STORE_SELECTION_PLATFORMS.includes(platformName);
    const showOtherPlatformSelector = isOtherBucketSite;
    const storeDetailsUrl = displayAsPublished || showOtherPlatformSelector ? site.url || '' : '';
    const [storeUrlDraft, setStoreUrlDraft] = useState(storeDetailsUrl);
    const effectiveSite = storeDetailsUrl !== storeUrlDraft ? { ...site, url: storeUrlDraft } : site;
    const showManualPublicId = !displayAsPublished && !showOtherPlatformSelector;
    const storeUrlDraftDirty = storeDetailsUrl !== storeUrlDraft;
    const canSave =
        !!appName &&
        isSiteReadyToSave(effectiveSite) &&
        (isDraftSite || siteDirty || storeUrlDraftDirty) &&
        !hasValidationErrors &&
        !site.longnameDuplicationDetails &&
        !site.isSaving &&
        !site.isLoadingStoreDetails;
    const hasSdkProduct = !!(userData?.attribution_enabled || userData?.sdk_enabled);
    const showSdkMetadata = !!site.id && !!configuration.apsalar_enabled && hasSdkProduct;
    const showOtherLatestSdkVersion = showOtherPlatformSelector && showSdkMetadata && !!site.sdk_integrated;
    const showSdkVersion =
        !showOtherPlatformSelector && !isManualPublicIdPlatform && showSdkMetadata && !!site.sdk_integrated;
    const showSdkNotIntegrated =
        !showOtherPlatformSelector && !isManualPublicIdPlatform && showSdkMetadata && !site.sdk_integrated;
    const showMappingKey = !showOtherPlatformSelector && !!appsPermissions.appKeywordEnabled && !!site.id;
    const showFraudControls = !!appsPermissions.fraudPermitted && !readOnly && isIosOrAndroid(site);
    const purchaseEnforcementHasDate = !!site.iap_enforcement_enabled && !!site.iap_enforcement_date;
    const previousStoreDetailsUrlRef = useRef(storeUrlDraft);
    const refreshStoreDetailsRef = useRef(onRefreshStoreDetails);

    useEffect(() => {
        setStoreUrlDraft(storeDetailsUrl);
    }, [storeDetailsUrl]);

    useEffect(() => {
        refreshStoreDetailsRef.current = onRefreshStoreDetails;
    }, [onRefreshStoreDetails]);

    useEffect(() => {
        if (readOnly || previousStoreDetailsUrlRef.current === storeUrlDraft) {
            previousStoreDetailsUrlRef.current = storeUrlDraft;
            return undefined;
        }

        previousStoreDetailsUrlRef.current = storeUrlDraft;
        const timeoutId = window.setTimeout(() => {
            refreshStoreDetailsRef.current(storeUrlDraft);
        }, STORE_DETAILS_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [readOnly, storeUrlDraft]);

    return (
        <div className={css.siteDetails}>
            <PlatformSelector
                isPublished={isPublished}
                platformName={platformName}
                platformNames={platformNames}
                platforms={platforms}
                readOnly={readOnly}
                selectedBucketPlatform={selectedBucketPlatform}
                showSelectionMethod={showSelectionMethod}
                siteId={site.id}
                onSitePatch={onSitePatch}
            />

            <SiteIdentityFields
                site={site}
                displayAsPublished={displayAsPublished}
                platformName={platformName}
                platforms={platforms}
                readOnly={readOnly}
                showManualPublicId={showManualPublicId}
                showMappingKey={showMappingKey}
                showOtherLatestSdkVersion={showOtherLatestSdkVersion}
                showOtherPlatformSelector={showOtherPlatformSelector}
                showSdkNotIntegrated={showSdkNotIntegrated}
                showSdkVersion={showSdkVersion}
                storeUrlDraft={storeUrlDraft}
                validationErrors={validationErrors}
                onSitePatch={onSitePatch}
                onStoreUrlDraftChange={setStoreUrlDraft}
            />

            {showFraudControls ? (
                <FraudControls
                    platformFamilyName={platformFamilyName}
                    purchaseEnforcementHasDate={purchaseEnforcementHasDate}
                    readOnly={readOnly}
                    site={site}
                    onSitePatch={onSitePatch}
                />
            ) : null}

            <AdvancedSettings
                site={site}
                configuration={configuration}
                appsPermissions={appsPermissions}
                adminMode={adminMode}
                translate={translate}
                userData={userData}
                readOnly={readOnly}
                onSitePatch={onSitePatch}
                onTestFcmIntegration={onTestFcmIntegration}
                validationErrors={validationErrors}
                appId={appId}
                appName={appName}
                onTrackAppsEvent={onTrackAppsEvent}
            />

            <div className={css.formActions}>
                <Button type={readOnly ? 'primary' : 'secondary'} level="level2" onClick={onCancel}>
                    <Translate id="STATIC.BUTTONS.CANCEL" />
                </Button>
                {!readOnly ? (
                    <Button
                        type="primary"
                        level="level2"
                        disabled={!canSave}
                        showSpinner={!!site.isSaving}
                        onClick={() => onSave(effectiveSite)}
                    >
                        <Translate id="STATIC.BUTTONS.SAVE" />
                    </Button>
                ) : null}
                {site.saveError ? <span className={css.saveError}>{site.saveError}</span> : null}
                <DuplicateLongnameMessage
                    details={site.longnameDuplicationDetails}
                    onJumpToDuplicateSite={onJumpToDuplicateSite}
                    onRestoreDuplicateSite={onRestoreDuplicateSite}
                />
            </div>
        </div>
    );
};

SiteDetails.propTypes = {
    adminMode: PropTypes.bool.isRequired,
    appId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    appName: PropTypes.string.isRequired,
    appsPermissions: appsPermissionsPropType.isRequired,
    configuration: configurationPropType.isRequired,
    onCancel: PropTypes.func.isRequired,
    onJumpToDuplicateSite: PropTypes.func.isRequired,
    onRefreshStoreDetails: PropTypes.func.isRequired,
    onRestoreDuplicateSite: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onSitePatch: PropTypes.func.isRequired,
    onTestFcmIntegration: PropTypes.func.isRequired,
    onTrackAppsEvent: PropTypes.func.isRequired,
    platformNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    platforms: PropTypes.arrayOf(platformPropType).isRequired,
    readOnly: PropTypes.bool.isRequired,
    site: sitePropType.isRequired,
    siteDirty: PropTypes.bool.isRequired,
    translate: PropTypes.func.isRequired,
    userData: userDataPropType.isRequired,
};

export default SiteDetails;
